<template>
  <d-modal class="purchase-add-popup-modal">
    <d-modal-header>
      <d-modal-title>
        {{ $t('purchase.addOrderForProduct') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-form v-if="loadedProductProperties && !loadingInProgress && !resultsDisplay">
        <validation-observer ref="purchase-add-form" tag="form">
          <div class="form-group">
            <label>
              {{ $t('purchase.numberOfPiecesNeeded') }}:
            </label>

            <validation-provider
              rules="required|min_value:1"
              name="quantity"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                type="number"
                :min="1"
                :step="1"
                v-model="quantity" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <purchase-select-shipper
            v-if="purchaseID === 0"
            ref="purchase-shipper"
            :isSub="false"
            :customerID="customerID"
            :read-only="false" />

          <purchase-select-shipper
            v-if="purchaseID === 0"
            ref="purchase-subshipper"
            :isSub="true"
            :customerID="subCustomerID"
            :read-only="false" />

          <div
            v-if="purchaseID === 0 && loadedPurchasesToUse && listOfPurchasesToUse.length"
            class="form-group">
            <label>
              <small>{{ $t('purchase.assignProductToExistingOrder') }}:</small>
            </label>

            <div :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                :options="listOfPurchasesToUse"
                v-model="purchaseToUse"
                :placeholder="$t('order.selectOrder')"
                :reduce="purchase => purchase.id"
                label="orders_names">
                <template slot="no-options">
                  {{ $t('purchase.noSuchOrder') }}&hellip;
                </template>
                <template slot="option" slot-scope="option">
                  <div class="d-center">
                    <template v-if="option.date">{{ option.date }} - {{ option.order_name }} (#{{ option.id }})</template>
                    <template v-else>{{ $t('purchase.noDeliveryDate') }} - {{ option.order_name }} (#{{ option.id }})</template>
                  </div>
                </template>
                <template slot="selected-option" slot-scope="option">
                  <div class="selected d-center">
                    <template v-if="option.date">{{ option.date }} - {{ option.order_name }} - (#{{ option.id }})</template>
                    <template v-else>{{ $t('purchase.noDeliveryDate') }} - {{ option.order_name }} (#{{ option.id }})</template>
                  </div>
                </template>
              </v-select>
            </div>
          </div>

          <div
            v-if="purchaseID > 0 && loadedPurchaseData"
            class="form-group">
            <label>
              <small>{{ $t('purchase.assignProductToExistingOrder') }}:</small>
            </label>
            <p>
              <strong class="text-primary">
                {{ preloadedPurchase.customer_name }} <template v-if="preloadedPurchase.date">({{ preloadedPurchase.date }})</template>
                <template v-if="preloadedPurchase.sub_customer_name">
                  <br>
                  <small>
                    {{ $t('ui.subSupplier') }}: <strong>{{ preloadedPurchase.sub_customer_name }}</strong>
                  </small>
                </template>
              </strong>
            </p>
          </div>

          <div
            v-if="showAdditionalFields"
            class="form-group">
            <d-checkbox
              inline
              v-model="pattern_required"
              toggle
              toggle-small>
              <small>{{ $t('purchase.requiresShape') }}</small>
            </d-checkbox>
          </div>

          <div
            v-if="showAdditionalFields"
            class="form-group">
            <d-checkbox
              inline
              v-model="pattern_stays"
              toggle
              toggle-small>
              <small>{{ $t('purchase.shapeStaysInHafciarnia') }}</small>
            </d-checkbox>
          </div>

          <div
            v-if="showAdditionalFields"
            class="form-group">
            <label>
              <small>{{ $t('purchase.size.size') }}:</small>
            </label>

            <div :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                :options="getSizeForProduct(productID)"
                v-model="size_id"
                :placeholder="$t('purchase.size.selectSize')"
                :reduce="size => size.value"
                label="text"
                :taggable="true"
                @option:created="addNewSizeToSewingRoom">
                <template slot="no-options">
                  {{ $t('purchase.size.noSuchSize') }}&hellip;
                </template>
                <template slot="option" slot-scope="option">
                  <div class="d-center">
                    {{ option.text }}
                    <template v-if="isNewOption('sizes', option.text)">
                      ({{ $t('ui.addNew') }})
                    </template>
                  </div>
                </template>
                <template slot="selected-option" slot-scope="option">
                  <div class="selected d-center">
                    {{ option.text }}
                  </div>
                </template>
              </v-select>
            </div>
          </div>

          <div
            v-if="showAdditionalFields"
            class="form-group">
            <label>
              <small>{{ $t('purchase.fabric.fabric') }}:</small>
            </label>

            <div :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                :options="availableMaterials"
                v-model="material_id"
                :placeholder="$t('purchase.fabric.chooseFabric')"
                label="text"
                :reduce="material => material.value"
                :taggable="true"
                @option:created="addNewMaterialToSewingRoom">
                <template slot="no-options">
                  {{ $t('purchase.fabric.noSuchFabric') }}&hellip;
                </template>
                <template slot="option" slot-scope="option">
                  <div class="d-center">
                    {{ option.text }}
                    <template v-if="isNewOption('materials', option.text)">
                      ({{ $t('ui.addNew') }})
                    </template>
                  </div>
                </template>
                <template slot="selected-option" slot-scope="option">
                  <div class="selected d-center">
                    {{ option.text }}
                  </div>
                </template>
              </v-select>
            </div>
          </div>

          <div
            v-if="showAdditionalFields"
            class="form-group">
            <label>
              <small>{{ $t('purchase.fabric.fabricColor') }}:</small>
            </label>

            <div :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                :options="getColorsForMaterial(material_id)"
                v-model="color_id"
                :disabled="!material_id"
                :placeholder="$t('purchase.fabric.chooseColor')"
                label="text"
                :reduce="color => color.value"
                :taggable="true"
                @option:created="addNewColorToSewingRoom">
                <template slot="no-options">
                  {{ $t('purchase.fabric.noSuchColor') }}&hellip;
                </template>
                <template slot="option" slot-scope="option">
                  <div class="d-center">
                    {{ option.text }}
                    <template v-if="isNewOption('colors', option.text)">
                      ({{ $t('ui.addNew') }})
                    </template>
                  </div>
                </template>
                <template slot="selected-option" slot-scope="option">
                  <div class="selected d-center">
                    {{ option.text }}
                  </div>
                </template>
              </v-select>
            </div>
          </div>

          <div
            v-if="showAdditionalFields"
            class="form-group">
            <label>
              <small>{{ $t('purchase.fleece.fleece') }}:</small>
            </label>
            <div :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                :options="availablePolars"
                v-model="polar_id"
                :placeholder="$t('purchase.fleece.chooseFleece')"
                label="text"
                :reduce="polar => polar.value"
                :taggable="true"
                @option:created="addNewPolarToSewingRoom">
                <template slot="no-options">
                  {{ $t('purchase.fleece.noSuchFleece') }}&hellip;
                </template>
                <template slot="option" slot-scope="option">
                  <div class="d-center">
                    {{ option.text }}
                    <template v-if="isNewOption('polars', option.text)">
                      ({{ $t('ui.addNew') }})
                    </template>
                  </div>
                </template>
                <template slot="selected-option" slot-scope="option">
                  <div class="selected d-center">
                    {{ option.text }}
                  </div>
                </template>
              </v-select>
            </div>
          </div>

          <div
            v-if="showAdditionalFields"
            class="form-group">
            <label>
              <small>{{ $t('purchase.stitching.stitching') }}:</small>
            </label>
            <div :class="{ 'form-control': true, 'is-select': true }">
              <v-select
                :options="availableSeams"
                v-model="seam_id"
                :placeholder="$t('purchase.stitching.chooseStitching')"
                label="text"
                :taggable="true"
                :reduce="seam => seam.value"
                @option:created="addNewSeamToSewingRoom">
                <template slot="no-options">
                  {{ $t('purchase.stitching.noSuchStitching') }}&hellip;
                </template>
                <template slot="option" slot-scope="option">
                  <div class="d-center">
                    {{ option.text }}
                    <template v-if="isNewOption('seams', option.text)">
                      ({{ $t('purchase.addNew') }})
                    </template>
                  </div>
                </template>
                <template slot="selected-option" slot-scope="option">
                  <div class="selected d-center">
                    {{ option.text }}
                  </div>
                </template>
              </v-select>
            </div>
          </div>
        </validation-observer>

        <div
          v-if="!purchaseToUse"
          class="form-group">
          <div>
            <label for="inputOrder">{{ $t('attachment.filesToAttach') }}:</label>
          </div>

          <div
            v-for="(file, index) of files"
            :key="'file-to-attach-' + index"
            class="mt-2 d-flex">
            <file-input
              :id="'inputOrderAttachment-' + index"
              :key="'attached-file-' + index"
              :label="$t('attachment.chooseFileToAttachOptional')"
              :onChange="(e) => setFile(e, index)" />

            <d-button
              theme="danger"
              :outline="true"
              class="text-nowrap ml-2"
              @click.prevent="removeFile(index)">
              {{ $t('purchase.removeFile') }}
            </d-button>
          </div>

          <d-button
            theme="success"
            :outline="true"
            @click.prevent="files.push('')"
            class="mt-2">
            {{ $t('attachment.addAttachemnt') }}
          </d-button>
        </div>
      </d-form>

      <div
        v-if="!loadedProductProperties"
        class="text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <div
        v-if="loadingInProgress"
        class="text-center">
        {{ $t('purchase.addingOrderInProgress') }}&hellip;
      </div>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        v-if="!loadingInProgress && !loadingPurchasesInProgress"
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm">
        <template v-if="purchaseToUse">{{ $t('purchase.addProductsToExistingOrder') }}</template>
        <template v-else>{{ $t('purchase.addOrder') }}</template>
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import PurchaseSelectShipper from './../purchases/PurchaseSelectShipper.vue';
import Vue from 'vue';

export default {
  name: 'purchase-add-popup',
  props: [
    'productID',
    'purchaseID',
    'orderID',
    'orderProductID',
    'missingAmount',
    'customerID',
    'subCustomerID'
  ],
  components: {
    'purchase-select-shipper': PurchaseSelectShipper
  },
  computed: {
    availableMaterials () {
      if (!this.productProperties || !this.productProperties.materials) {
        return [];
      }

      return this.productProperties.materials.map(material => ({
        text: material.name,
        value: material.id
      }));
    },
    availablePolars () {
      if (!this.productProperties || !this.productProperties.polars) {
        return [];
      }

      return this.productProperties.polars.map(polar => ({
        text: polar.name,
        value: polar.id
      }));
    },
    availableSeams () {
      if (!this.productProperties || !this.productProperties.seams) {
        return [];
      }

      return this.productProperties.seams.map(seam => ({
        text: seam.name,
        value: seam.id
      }));
    },
    availableMaterialNames () {
      if (this.productProperties && this.productProperties.materials) {
        return this.productProperties.materials.map(material => material.name);
      }

      return [];
    },
    availableSizeNames () {
      let sizes = this.getSizeForProduct(this.productID);

      if (sizes) {
        return sizes.map(size => size.text);
      }

      return [];
    },
    availablePolarNames () {
      if (this.productProperties && this.productProperties.polars) {
        return this.productProperties.polars.map(polar => polar.name);
      }

      return [];
    },
    availableSeamNames () {
      if (this.productProperties && this.productProperties.seams) {
        return this.productProperties.seams.map(seam => seam.name);
      }

      return [];
    },
    availableColorNames () {
      if (this.productProperties && this.productProperties.materials && this.material_id) {
        let foundedMaterial = this.productProperties.materials.filter(material => material.id === this.material_id);

        if (foundedMaterial[0]) {
          return foundedMaterial[0].colors.map(color => color.name);
        }

        return [];
      }

      return [];
    }
  },
  data () {
    return {
      pattern_required: false,
      pattern_stays: false,
      size_id: 0,
      material_id: 0,
      color_id: 0,
      polar_id: 0,
      seam_id: 0,
      quantity: 1,
      files: [],
      loadingInProgress: false,
      loadedProductProperties: false,
      productProperties: [],
      resultsDisplay: false,
      showAdditionalFields: false,
      listOfPurchasesToUse: [],
      loadingPurchasesInProgress: false,
      loadedPurchasesToUse: false,
      purchaseToUse: null,
      predefinedCustomerID: 0,
      predefinedSubCustomerID: 0,
      preloadedPurchase: null,
      loadedPurchaseData: false
    };
  },
  mounted () {
    if (this.purchaseID > 0) {
      this.setPurchaseAtStart();
    }

    this.quantity = this.missingAmount;
    this.order_files = [];

    if (this.quantity < 0) {
      this.quantity = 0;
    }

    this.loadAdditionalData();
    this.$bus.$on('purchase-select-shipper-sewing-room-integration-change', this.toggleAdditionalFields);
    this.$bus.$on('purchase-select-shipper-changed', this.purchaseShipperChanged);
  },
  methods: {
    validateForm () {
      let customerID = this.$refs['purchase-shipper'].customer_id;
      let subCustomerID = this.$refs['purchase-subshipper'].customer_id;

      if (
        !this.purchaseToUse &&
        (
          (
            customerID &&
            !subCustomerID &&
            this.listOfPurchasesToUse.length
          ) || (
            customerID &&
            subCustomerID &&
            this.listOfPurchasesToUse.filter(purchase => purchase.sub_customer_id === parseInt(subCustomerID, 10)).length
          )
        )
      ) {
        Vue.swal({
          title: this.$t('purchase.existingOrdersTitle'),
          text: this.$t('purchase.existingOrdersDesc'),
          type: 'question',
          showCancelButton: true,
          confirmButtonText: this.$t('purchase.existingOrdersPleaseAdd'),
          cancelButtonText: this.$t('ui.cancel')
        }).then((result) => {
          if (result.value) {
            FormUtils.validate(this.$refs['purchase-add-form'], this.submitForm);
          }
        });
      } else {
        FormUtils.validate(this.$refs['purchase-add-form'], this.submitForm);
      }
    },
    submitForm () {
      if (this.purchaseToUse) {
        this.addProductToPurchase();
      } else {
        this.addPurchase();
      }
    },
    addProductToPurchase () {
      this.loadingInProgress = true;

      let productDataToPurchase = {
        product_id: this.productID,
        quantity: parseInt(this.quantity, 10),
        vat_rate: '23.00'
      };

      if (this.showAdditionalFields) {
        productDataToPurchase.pattern_required = +this.pattern_required;
        productDataToPurchase.pattern_stays = +this.pattern_stays;
        productDataToPurchase.size_id = this.size_id;
        productDataToPurchase.material_id = this.material_id;
        productDataToPurchase.color_id = this.color_id;
        productDataToPurchase.polar_id = this.polar_id;
        productDataToPurchase.seam_id = this.seam_id;
      }

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/purchases/add-product-to-purchase',
        formData: {
          order_id: parseInt(this.orderID, 10),
          purchase_id: parseInt(this.purchaseToUse, 10),
          product: productDataToPurchase
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          this.loadingInProgress = false;

          if (response.success) {
            this.$bus.$emit(
              'order-product-purchase-reservation',
              this.productID,
              this.orderProductID,
              parseInt(response.item.id, 10),
              parseInt(this.purchaseToUse, 10),
              parseInt(this.quantity, 10),
              response.item
            );

            Vue.swal({
              title: this.$t('purchase.addProductOrderToOrderSuccessTitle'),
              html: this.$t('purchase.addProductOrderToOrderSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });

            this.loadAdditionalData();
            this.cancel();
          } else {
            Vue.swal({
              title: this.$t('purchase.addProductToOrderErrorTitle'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.loadingInProgress = false;
          Vue.swal({
            title: this.$t('purchase.addProductToOrderErrorTitle'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    addPurchase () {
      this.loadingInProgress = true;

      let productDataToPurchase = {
        product_id: this.productID,
        quantity: parseInt(this.quantity, 10),
        vat_rate: '23.00'
      };

      if (this.showAdditionalFields) {
        productDataToPurchase.pattern_required = +this.pattern_required;
        productDataToPurchase.pattern_stays = +this.pattern_stays;
        productDataToPurchase.size_id = this.size_id;
        productDataToPurchase.material_id = this.material_id;
        productDataToPurchase.color_id = this.color_id;
        productDataToPurchase.polar_id = this.polar_id;
        productDataToPurchase.seam_id = this.seam_id;
      }

      let filesToSend = [];

      if (this.files.length && !this.purchaseToUse) {
        for (let i = 0; i < this.files.length; i++) {
          filesToSend.push({
            key: 'files[' + i + ']',
            value: this.files[i]
          });
        }
      }

      let customerID = 0;
      let subCustomerID = 0;

      if (this.purchaseID > 0) {
        customerID = this.predefinedCustomerID;
        subCustomerID = this.predefinedSubCustomerID;
      } else {
        customerID = this.$refs['purchase-shipper'].customer_id;
        subCustomerID = this.$refs['purchase-subshipper'].customer_id;
      }

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/purchases/store',
        formData: {
          customer_id: customerID,
          sub_customer_id: subCustomerID,
          products: [
            productDataToPurchase
          ]
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          this.loadingInProgress = false;

          if (response.success) {
            this.$bus.$emit(
              'order-product-purchase-reservation',
              this.productID,
              this.orderProductID,
              parseInt(response.item.products[0].id, 10),
              parseInt(response.item.id, 10),
              parseInt(this.quantity, 10),
              response.item.products[0]
            );

            Vue.swal({
              title: this.$t('purchase.addOrderSuccessTitle'),
              html: this.$t('purchase.addProductOrderSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });

            this.loadAdditionalData();
            this.cancel();
          } else {
            Vue.swal({
              title: this.$t('purchase.addOrderErrorTitle'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.loadingInProgress = false;
          Vue.swal({
            title: this.$t('purchase.addOrderErrorTitle'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      }, filesToSend);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/sewing-room/items',
        loadedKey: 'loadedProductProperties',
        outputKey: 'productProperties',
        errorKey: 'loadError'
      });
    },
    cancel () {
      this.quantity = 0;
      this.$bus.$emit('purchase-add-popup-close');
    },
    toggleAdditionalFields (newValue) {
      this.showAdditionalFields = newValue;
    },
    getSizeForProduct (productID) {
      productID = parseInt(productID, 10);

      if (this.productProperties && this.productProperties.products_sizes && this.productProperties.products_sizes[productID]) {
        return this.productProperties.products_sizes[productID].map(size => ({
          text: size.name,
          value: size.id
        }));
      }

      return [];
    },
    getColorsForMaterial (materialID) {
      materialID = parseInt(materialID, 10);

      if (this.productProperties && this.productProperties.materials) {
        let foundedMaterial = this.productProperties.materials.filter(material => material.id === materialID);

        if (foundedMaterial[0]) {
          return foundedMaterial[0].colors.map(color => ({
            text: color.name,
            value: color.id
          }));
        }

        return [];
      }

      return [];
    },
    addNewMaterialToSewingRoom (newMaterial) {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/sewing-room/store-material',
        formData: {
          name: newMaterial.text
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        skipErrorDisplay: true,
        successAction: (response) => {
          if (response.success) {
            if (!response.item.colors) {
              response.item.colors = [];
            }

            this.productProperties.materials.push(response.item);
            this.material_id = response.item.id;
            Vue.swal({
              title: this.$t('purchase.fabric.addFabricSuccessTitle'),
              html: this.$t('purchase.fabric.addFabricSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });
          } else {
            this.material_id = null;

            Vue.swal({
              title: this.$t('purchase.fabric.addFabricErrorText'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.material_id = null;

          Vue.swal({
            title: this.$t('purchase.fabric.addFabricErrorText'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    addNewSizeToSewingRoom (newSize) {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/sewing-room/store-size',
        formData: {
          name: newSize && newSize.text ? newSize.text : newSize,
          product_id: this.productID
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        skipErrorDisplay: true,
        successAction: (response) => {
          if (response.success) {
            this.productProperties.products_sizes[this.productID].push({
              id: response.item.id,
              name: response.item.name
            });
            this.size_id = response.item.id;

            Vue.swal({
              title: this.$t('purchase.size.addSizeSuccessTitle'),
              html: this.$t('purchase.size.addSizeSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });
          } else {
            this.size_id = null;

            Vue.swal({
              title: this.$t('purchase.size.addSizeErrorText'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.size_id = null;

          Vue.swal({
            title: this.$t('purchase.size.addSizeErrorText'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    addNewColorToSewingRoom (newColor) {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/sewing-room/store-color',
        formData: {
          name: newColor && newColor.text ? newColor.text : newColor,
          material_id: this.material_id
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        skipErrorDisplay: true,
        successAction: (response) => {
          if (response.success) {
            let foundedIndex = this.productProperties.materials.findIndex(material => material.id === this.material_id);

            if (foundedIndex > -1) {
              this.productProperties.materials[foundedIndex].colors.push(response.item);
            }

            this.color_id = response.item.id;

            Vue.swal({
              title: this.$t('purchase.fabric.addFabricColorSuccessTitle'),
              html: this.$t('purchase.fabric.addFabricColorSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });
          } else {
            this.color_id = null;

            Vue.swal({
              title: this.$t('purchase.fabric.addFabricColorErrorText'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.color_id = null;

          Vue.swal({
            title: this.$t('purchase.fabric.addFabricColorErrorText'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    addNewSeamToSewingRoom (newSeam) {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/sewing-room/store-seam',
        formData: {
          name: newSeam.text
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        skipErrorDisplay: true,
        successAction: (response) => {
          if (response.success) {
            this.productProperties.seams.push(response.item);
            this.seam_id = response.item.id;
            Vue.swal({
              title: this.$t('purchase.stitching.addStitchingSuccessTitle'),
              html: this.$t('purchase.stitching.addStitchingSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });
          } else {
            this.seam_id = null;

            Vue.swal({
              title: this.$t('purchase.stitching.addStitchingErrorText'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.seam_id = null;

          Vue.swal({
            title: this.$t('purchase.stitching.addStitchingErrorText'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    addNewPolarToSewingRoom (newPolar) {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/sewing-room/store-polar',
        formData: {
          name: newPolar.text
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        skipErrorDisplay: true,
        successAction: (response) => {
          if (response.success) {
            this.productProperties.polars.push(response.item);
            this.polar_id = response.item.id;
            Vue.swal({
              title: this.$t('purchase.fleece.addFleeceSuccessTitle'),
              html: this.$t('purchase.fleece.addFleeceSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });
          } else {
            this.polar_id = null;

            Vue.swal({
              title: this.$t('purchase.fleece.addFleeceErrorText'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.polar_id = null;

          Vue.swal({
            title: this.$t('purchase.fleece.addFleeceErrorText'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    isNewOption (type, valueToCheck) {
      if (type === 'materials') {
        return this.availableMaterialNames.indexOf(valueToCheck) === -1;
      } else if (type === 'polars') {
        return this.availablePolarNames.indexOf(valueToCheck) === -1;
      } else if (type === 'seams') {
        return this.availableSeamNames.indexOf(valueToCheck) === -1;
      } else if (type === 'colors') {
        return this.availableColorNames.indexOf(valueToCheck) === -1;
      } else if (type === 'sizes') {
        return this.availableSizeNames.indexOf(valueToCheck) === -1;
      }

      return false;
    },
    purchaseShipperChanged (newPurchaseShipperID) {
      if (newPurchaseShipperID !== null) {
        this.checkForNewPurchases(newPurchaseShipperID);
      } else {
        this.listOfPurchasesToUse = [];
      }
    },
    checkForNewPurchases (purchaseShipperID) {
      this.loadingPurchasesInProgress = true;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/purchases/items',
        loadedKey: 'loadedPurchasesToUse',
        outputKey: 'listOfPurchasesToUse',
        errorKey: 'loadError',
        params: {
          pagination: 0,
          where: {
            status: 'NEW',
            main_customer_id: purchaseShipperID
          }
        },
        successAction: () => {
          this.purchaseToUse = null;
          this.loadingPurchasesInProgress = false;
        },
        failAction: () => {
          this.purchaseToUse = null;
          this.loadingPurchasesInProgress = false;
        }
      });
    },
    setFile (e, index) {
      this.files[index] = e.target.files[0];
    },
    removeFile (index) {
      this.files.splice(index, 1);
    },
    setPurchaseAtStart () {
      this.purchaseToUse = this.purchaseID;
      this.loadPurchaseData();
    },
    loadPurchaseData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/purchases/item/' + this.purchaseID,
        loadedKey: 'loadedPurchaseData',
        outputKey: 'preloadedPurchase',
        errorKey: 'loadError',
        successAction: () => {
          this.predefinedCustomerID = this.preloadedPurchase.customer_id;
          this.predefinedSubCustomerID = this.preloadedPurchase.sub_customer_id;

          if (this.preloadedPurchase.customer_has_integration) {
            this.showAdditionalFields = true;
          }
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('purchase-select-shipper-sewing-room-integration-change', this.toggleAdditionalFields);
    this.$bus.$off('purchase-select-shipper-changed', this.purchaseShipperChanged);
  }
}
</script>

<style lang="scss">
.purchase-add-popup-modal {
  button.close {
    display: none;
  }
}
</style>
